import { Injectable } from '@angular/core';
import { MeetingExpenseDataService } from '@omni/data-services/meeting-expense/meeting-expense.data.service';
import { DeltaService, EntityNames, EntitySyncInfo } from '@omni/data-services/delta/delta.service';
import { AuthenticationService } from '@omni/services/authentication.service';
import { DiskService } from '@omni/services/disk/disk.service';
import { DeviceService } from '@omni/services/device/device.service';
import { DB_SYNC_STATE_KEYS } from '@omni/config/pouch-db.config';
import { MEETING_EXPENSE_ENTITY_SET } from '@omni/classes/meeting-expense/meeting-expense.constants';
import { idsMissingFromDevice } from '@omni/classes/meeting-expense/meeting-expense.pending';
import { MeetingExpenseService } from './meeting-expense.service';

@Injectable({ providedIn: 'root' })
export class MeetingExpenseSyncService {
  constructor(
    private readonly dataService: MeetingExpenseDataService,
    private readonly expenseService: MeetingExpenseService,
    private readonly authService: AuthenticationService,
    private readonly deltaService: DeltaService,
    private readonly disk: DiskService,
    private readonly device: DeviceService,
  ) {}

  async sync(forceFullSync = false, loadFromDBOnly = false): Promise<void> {
    if (!this.expenseService.isEnabled()) return this.disable();
    if (!loadFromDBOnly && !this.device.isOffline) await this.pull(forceFullSync);
    await this.dataService.recountPending();
    await this.expenseService.rebuildIndexFromCache();
  }

  private async disable(): Promise<void> {
    await this.dataService.purgeSynced();
    this.expenseService.clearSessionState();
    await this.dataService.recountPending();
  }

  private async pull(forceFullSync: boolean): Promise<void> {
    const syncState = (await this.disk.getSyncState(DB_SYNC_STATE_KEYS.SYNC_MEETING_EXPENSES))
      ?? { _id: DB_SYNC_STATE_KEYS.SYNC_MEETING_EXPENSES, lastUpdatedTime: null };
    const isInitial = forceFullSync || !syncState.lastUpdatedTime;
    const startedAt = Date.now();
    const syncInfo: EntitySyncInfo = { entityName: EntityNames.meetingExpense, totalFailed: 0, totalSynced: 0, errors: [], syncStatus: true };
    try {
      await this.expenseService.loadMasterData(true, true);
      // The same meeting-date range the activity sync uses, so expenses stay on the device exactly as long as their meeting.
      const range = this.authService.getFromToDateRangeInUTCMiliSec(undefined);
      const meetingWindow = { fromIso: new Date(Number(range.from)).toISOString(), toIso: new Date(Number(range.to)).toISOString() };
      const modifiedSince = isInitial ? null : new Date(syncState.lastUpdatedTime).toISOString();
      // Read first: a doc cached or uploaded while the fetches run would otherwise be pruned against results that predate it.
      const cached = await this.dataService.allDocs();
      const [changed, liveIds] = await Promise.all([
        this.dataService.fetchForSync(meetingWindow, modifiedSince),
        isInitial ? null : this.dataService.fetchLiveIdsForSync(meetingWindow),
      ]);
      const missing = liveIds ? idsMissingFromDevice(liveIds, cached, changed) : [];
      const filled = missing.length ? await this.dataService.fetchForSyncByIds(missing).catch((err) => {
        console.error('MeetingExpenseSyncService.pull fill failed; retried next sync', err);
        return [];
      }) : [];
      const expenses = [...changed, ...filled];
      await this.dataService.applySynced(cached, expenses, liveIds ?? new Set(expenses.map((expense) => expense.id)));
      syncInfo.totalSynced = expenses.length;
      await this.disk.updateSyncState({ ...syncState, lastUpdatedTime: startedAt }, true);
    } catch (err) {
      console.error('MeetingExpenseSyncService.pull failed', err);
      this.deltaService.addSyncErrorToEntitySyncInfo(syncInfo, MEETING_EXPENSE_ENTITY_SET, err);
    }
    this.deltaService.addEntitySyncInfo(syncInfo);
  }
}
